/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import entries from 'lodash/entries';
import invert from 'lodash/invert';
import isEqual from 'lodash/isEqual';
import toLower from 'lodash/toLower';
import isPlainObject from 'lodash/isPlainObject';
import flatten from 'lodash/flatten';

import {
  DAYTIME,
  EVENING,
  FAX,
  MOBILE,
  PRIMARY_CONTACT
} from 'services/ContactMethodMaintenance';
import Content from 'services/Content';
import { trimString } from '../support/StringUtil';
import { removeUnchangedSelections } from 'components/layouts/Preferences/DocumentDelivery/DocumentDeliveryFilters';
import uniqBy from 'lodash/uniqBy';


/*
  This file contains functions which help map between the application state and the component state for Preferences objects.
  The application will receive preferences from the backend and store the object in Redux. Components that use the preferences
  may need to modify the shape of the state and these mappers help facilitate between them, including adding disposition objects
  to signal any changes made to the objects.
*/

export const RESIDENTIAL_ADDRESS = 103;
export const MAILING_ADDRESS = 100;

const defaultAddressComponentState = {
  addressType: '',
  addressOne: '',
  addressTwo: '',
  city: '',
  state: '',
  zipCode: '',
  zipCodeExt: '',
  militaryAddressOne: '',
  militaryAddressTwo: '',
  militaryCity: '',
  militaryState: '',
  militaryZipCode: '',
  militaryZipCodeExt: '',
  nonUSAddressOne: '',
  nonUSAddressTwo: '',
  nonUSCity: '',
  country: '',
  postalCode: ''
};

const defaultPhoneNumberComponentState = {
  daytimePhone: '',
  daytimePhoneExt: '',
  daytimePhoneCountryCode: '',
  eveningPhone: '',
  eveningPhoneExt: '',
  eveningPhoneCountryCode: '',
  mobilePhone: '',
  mobilePhoneCountryCode: '',
  faxNumber: '',
  faxNumberCountryCode: ''
};

const defaultEmailComponentState = {
  email: ''
};

export const defaultEmailAddressAppState = {
  address: '',
  contactId: 0,
  typeId: 100
};

export const defaultMailingAddressAppState = {
  country: 'US'
};

const defaultTrustedContactAppState = {
  accountId: '',
  productType: null,
  namePrefix: '',
  firstName: '',
  middleName: '',
  lastName: '',
  nameSuffix: '',
  mailingAddress: null,
  emailAddress: null,
  phoneNumber: {},
  relationship: '',
  disposition: null
};

export const groupByValueWithId = {
  product: 10,
  retirement: 20,
  ownership: 30,
  nonRetirement: 40
};

export const groupByIdMap = invert(groupByValueWithId);

export const appAddressToComponentAddressMapper = (appAddress, isMilitary) => {
  const componentAddressObject = {
    ...defaultAddressComponentState
  };
  const {
    addressLines = [],
    city,
    country,
    state,
    zipCode,
    zipCodeExt
  } = appAddress;
  const isForeign = country !== 'US';

  if (isForeign) {
    componentAddressObject.addressType = 'non-us';
    componentAddressObject.nonUSAddressOne = addressLines[0] || '';
    componentAddressObject.nonUSAddressTwo = addressLines[1] || '';
    componentAddressObject.nonUSCity = city || '';
    componentAddressObject.country = country || '';
    componentAddressObject.postalCode = zipCode || '';
  } else if (isMilitary) {
    componentAddressObject.addressType = 'military';
    componentAddressObject.militaryAddressOne = addressLines[0] || '';
    componentAddressObject.militaryAddressTwo = addressLines[1] || '';
    componentAddressObject.militaryCity = city || '';
    componentAddressObject.militaryState = state || '';
    componentAddressObject.militaryZipCode = zipCode || '';
    componentAddressObject.militaryZipCodeExt = zipCodeExt || '';
  } else {
    componentAddressObject.addressType = 'us';
    componentAddressObject.addressOne = addressLines[0] || '';
    componentAddressObject.addressTwo = addressLines[1] || '';
    componentAddressObject.city = city || '';
    componentAddressObject.state = state || '';
    componentAddressObject.zipCode = zipCode || '';
    componentAddressObject.zipCodeExt = zipCodeExt || '';
  }

  return componentAddressObject;
};

export const constructAccountVisibilityPayload = (
  accounts,
  defaultAccounts
) => {
  return accounts
    .filter((account) => {
      return defaultAccounts.some((a) => {
        return (
          a.accountId === account.accountId &&
          account.hiddenAccount !== a.hiddenAccount
        );
      });
    })
    .map((account) => {
      return {
        accountId: account.accountId,
        accountNumber: account.accountNumber,
        accountChecksum: account.accountChecksum,
        productType: account.productType,
        trpCode: account.trpCode,
        accountTypeId: account.accountTypeId,
        suppressFromView: account.hiddenAccount
      };
    });
};

export const createNewAddressFromComponentAddress = (componentAddress) => {
  const { addressType } = componentAddress;

  const addressLines = [];
  const foreign = addressType === 'non-us';
  const country = foreign ? componentAddress.country : 'US';
  let addressOne = '';
  let addressTwo = '';
  let city = '';
  let state = '';
  let zipCode = '';
  let zipCodeExt = '';

  if (addressType === 'us') {
    addressOne = componentAddress.addressOne;
    addressTwo = componentAddress.addressTwo;
    city = componentAddress.city;
    state = componentAddress.state;
    zipCode = componentAddress.zipCode;
    zipCodeExt = componentAddress.zipCodeExt;
  } else if (addressType === 'military') {
    addressOne = componentAddress.militaryAddressOne;
    addressTwo = componentAddress.militaryAddressTwo;
    city = componentAddress.militaryCity;
    state = componentAddress.militaryState;
    zipCode = componentAddress.militaryZipCode;
    zipCodeExt = componentAddress.militaryZipCodeExt;
  } else {
    addressOne = componentAddress.nonUSAddressOne;
    addressTwo = componentAddress.nonUSAddressTwo;
    city = componentAddress.nonUSCity;
    zipCode = componentAddress.postalCode;
    zipCodeExt = '0000';
  }

  addressOne = addressOne.trim();
  city = city.trim();
  state = state.trim();
  zipCode = zipCode.trim();

  if (zipCodeExt) {
    zipCodeExt = zipCodeExt.trim();
  }

  addressLines.push(addressOne);
  if (addressTwo) {
    addressTwo = addressTwo.trim();
    addressLines.push(addressTwo);
  }

  return {
    addressLines,
    city,
    country,
    foreign,
    state,
    zipCode,
    zipCodeExt
  };
};

export const componentAddressToAppAddressMapper = (
  componentAddress,
  appAddress
) => {
  const newAddress = createNewAddressFromComponentAddress(componentAddress);
  const { foreign } = newAddress;

  const oldAddress = {
    addressLines: appAddress.addressLines,
    city: appAddress.city,
    country: appAddress.country,
    foreign: appAddress.country !== 'US',
    state: appAddress.state,
    zipCode: appAddress.zipCode,
    zipCodeExt: appAddress.zipCodeExt
  };

  if (isEqual(newAddress, oldAddress)) {
    return {
      ...appAddress,
      foreign
    };
  }

  return {
    ...appAddress,
    ...newAddress,
    disposition: {
      action: 'update'
    }
  };
};

export const appPhoneNumberToComponentPhoneNumberMapper = (appPhoneNumbers) => {
  const componentPhoneNumberObject = {
    ...defaultPhoneNumberComponentState
  };

  if (Array.isArray(appPhoneNumbers)) {
    appPhoneNumbers.forEach((phoneNumber) => {
      const {
        areaCode,
        countryCode,
        exchange,
        extension,
        localNumber,
        typeId
      } = phoneNumber;
      const combinedPhoneNumber = `${areaCode}${exchange}${localNumber}`;
      switch (typeId) {
        case DAYTIME:
          componentPhoneNumberObject.daytimePhone = combinedPhoneNumber;
          componentPhoneNumberObject.daytimePhoneCountryCode =
            countryCode || '1';
          componentPhoneNumberObject.daytimePhoneExt = extension || '';
          break;
        case EVENING:
          componentPhoneNumberObject.eveningPhone = combinedPhoneNumber;
          componentPhoneNumberObject.eveningPhoneCountryCode =
            countryCode || '1';
          componentPhoneNumberObject.eveningPhoneExt = extension || '';
          break;
        case MOBILE:
          componentPhoneNumberObject.mobilePhone = combinedPhoneNumber;
          componentPhoneNumberObject.mobilePhoneCountryCode =
            countryCode || '1';
          break;
        case FAX:
          componentPhoneNumberObject.faxNumber = combinedPhoneNumber;
          componentPhoneNumberObject.faxNumberCountryCode = countryCode || '1';
          break;
        default:
          break;
      }
    });
  } else if (isPlainObject(appPhoneNumbers) && !isEmpty(appPhoneNumbers)) {
    const { areaCode, countryCode, exchange, extension, localNumber, typeId } =
      appPhoneNumbers;
    const combinedPhoneNumber = `${areaCode}${exchange}${localNumber}`;
    switch (typeId) {
      case DAYTIME:
        componentPhoneNumberObject.daytimePhone = combinedPhoneNumber;
        componentPhoneNumberObject.daytimePhoneCountryCode = countryCode || '1';
        componentPhoneNumberObject.daytimePhoneExt = extension || '';
        break;
      case EVENING:
        componentPhoneNumberObject.eveningPhone = combinedPhoneNumber;
        componentPhoneNumberObject.eveningPhoneCountryCode = countryCode || '1';
        componentPhoneNumberObject.eveningPhoneExt = extension || '';
        break;
      case MOBILE:
        componentPhoneNumberObject.mobilePhone = combinedPhoneNumber;
        componentPhoneNumberObject.mobilePhoneCountryCode = countryCode || '1';
        break;
      case FAX:
        componentPhoneNumberObject.faxNumber = combinedPhoneNumber;
        componentPhoneNumberObject.faxNumberCountryCode = countryCode || '1';
        break;
      default:
        break;
    }
  }

  return componentPhoneNumberObject;
};

const handleForeignNumberForUpdate = (appPhone, componentPhone, typeId) => {
  const fullBffNumber = `${appPhone.exchange}${appPhone.localNumber}`;
  const fullUiNumber = componentPhone.number;

  const sameNumber = fullUiNumber === fullBffNumber;
  const sameCountryCode = componentPhone.countryCode === appPhone.countryCode;

  if (sameNumber && sameCountryCode) {
    return {
      ...appPhone,
      us: false
    };
  }

  const isRemoving =
    fullBffNumber && !componentPhone.countryCode && !fullUiNumber;

  if (isRemoving) {
    return {
      ...appPhone,
      us: false,
      disposition: {
        action: 'remove'
      }
    };
  }

  const isAdding =
    appPhone.isEmpty && componentPhone.countryCode && !isEmpty(fullUiNumber);

  if (isAdding) {
    return {
      areaCode: '',
      countryCode: componentPhone.countryCode,
      disposition: {
        action: 'add'
      },
      exchange: '',
      extension: '',
      localNumber: fullUiNumber,
      typeId,
      us: false
    };
  }

  return {
    ...appPhone,
    areaCode: '',
    countryCode: componentPhone.countryCode,
    disposition: {
      action: 'update'
    },
    exchange: '',
    extension: '',
    localNumber: fullUiNumber,
    us: false
  };
};

export const updatePhoneNumber = (
  componentPhone,
  appPhone,
  isForeign,
  typeId
) => {
  if (isForeign) {
    return handleForeignNumberForUpdate(appPhone, componentPhone, typeId);
  }

  const fullBffNumber = `${appPhone.areaCode}${appPhone.exchange}${appPhone.localNumber}${appPhone.extension}`;
  let fullUiNumber = '';
  if (componentPhone && componentPhone.number) {
    fullUiNumber = componentPhone.number;
  }
  if (componentPhone && componentPhone.ext) {
    fullUiNumber = fullUiNumber + componentPhone.ext;
  }
  // existing phone - do nothing
  if (fullUiNumber === fullBffNumber) {
    return appPhone;
  }

  const isRemoving = fullBffNumber && !fullUiNumber;

  if (isRemoving) {
    return {
      ...appPhone,
      disposition: {
        action: 'remove'
      }
    };
  }

  const isAdding = appPhone.isEmpty && fullUiNumber;

  if (isAdding) {
    return {
      areaCode: componentPhone.number
        ? componentPhone.number.substring(0, 3)
        : '',
      countryCode: '1',
      disposition: {
        action: 'add'
      },
      exchange: componentPhone.number
        ? componentPhone.number.substring(3, 6)
        : '',
      extension: componentPhone.ext,
      foreignNumber: '',
      localNumber: componentPhone.number
        ? componentPhone.number.substring(6, 10)
        : '',
      typeId,
      us: true
    };
  }

  return {
    ...appPhone,
    areaCode: componentPhone.number
      ? componentPhone.number.substring(0, 3)
      : '',
    countryCode: '1',
    disposition: {
      action: 'update'
    },
    exchange: componentPhone.number
      ? componentPhone.number.substring(3, 6)
      : '',
    extension: componentPhone.ext,
    foreignNumber: '',
    localNumber: componentPhone.number
      ? componentPhone.number.substring(6, 10)
      : '',
    typeId,
    us: true
  };
};

export const simplePhoneNumberToAppPhoneNumber = (phoneNumber) => {
  return {
    areaCode: phoneNumber ? phoneNumber.substring(0, 3) : '',
    countryCode: '1',
    disposition: null,
    exchange: phoneNumber ? phoneNumber.substring(3, 6) : '',
    extension: '',
    foreignNumber: '',
    localNumber: phoneNumber ? phoneNumber.substring(6, 10) : '',
    typeId: 100,
    us: true
  };
};

export const getPhoneNumberByTypeOrEmpty = (phoneNumbers, type) => {
  const EMPTY_PHONE_OBJECT = {
    areaCode: '',
    countryCode: '',
    exchange: '',
    extension: '',
    localNumber: '',
    isEmpty: true
  };
  if (!phoneNumbers) {
    return EMPTY_PHONE_OBJECT;
  }
  const phone = phoneNumbers.find((p) => p.typeId === type);
  return phone || EMPTY_PHONE_OBJECT;
};

export const addUpdatedPhoneNumbers = (
  phoneNumbers,
  newPhone,
  existingPhone,
  isForeign,
  type
) => {
  if (phoneNumbers) {
    phoneNumbers.push(
      updatePhoneNumber(newPhone, existingPhone, isForeign, type)
    );
  }
};

export const componentPhoneNumberToAppPhoneNumberMapper = (
  componentPhoneNumbers,
  appPhoneNumbers,
  isForeign
) => {
  const phoneNumbers = [];

  const appDaytimePhone = getPhoneNumberByTypeOrEmpty(appPhoneNumbers, DAYTIME);
  const appEveningPhone = getPhoneNumberByTypeOrEmpty(appPhoneNumbers, EVENING);
  const appMobilePhone = getPhoneNumberByTypeOrEmpty(appPhoneNumbers, MOBILE);
  const appFaxPhone = getPhoneNumberByTypeOrEmpty(appPhoneNumbers, FAX);

  const uiDaytimePhone = {
    countryCode: componentPhoneNumbers.daytimePhoneCountryCode,
    ext: componentPhoneNumbers.daytimePhoneExt,
    number: componentPhoneNumbers.daytimePhone
  };
  const uiEveningPhone = {
    countryCode: componentPhoneNumbers.eveningPhoneCountryCode,
    ext: componentPhoneNumbers.eveningPhoneExt,
    number: componentPhoneNumbers.eveningPhone
  };
  const uiMobilePhone = {
    countryCode: componentPhoneNumbers.mobilePhoneCountryCode,
    ext: componentPhoneNumbers.mobilePhoneExt || '',
    number: componentPhoneNumbers.mobilePhone
  };
  const uiFaxPhone = {
    countryCode: componentPhoneNumbers.faxNumberCountryCode,
    ext: componentPhoneNumbers.faxNumberExt || '',
    number: componentPhoneNumbers.faxNumber
  };

  addUpdatedPhoneNumbers(
    phoneNumbers,
    uiDaytimePhone,
    appDaytimePhone,
    isForeign,
    DAYTIME
  );
  addUpdatedPhoneNumbers(
    phoneNumbers,
    uiEveningPhone,
    appEveningPhone,
    isForeign,
    EVENING
  );
  addUpdatedPhoneNumbers(
    phoneNumbers,
    uiMobilePhone,
    appMobilePhone,
    isForeign,
    MOBILE
  );
  addUpdatedPhoneNumbers(phoneNumbers, uiFaxPhone, appFaxPhone, isForeign, FAX);

  return phoneNumbers.filter((phoneNumber) => !phoneNumber.isEmpty);
};

export const appEmailToComponentEmailMapper = (appEmail) => {
  return {
    ...defaultEmailComponentState,
    email: appEmail.address || ''
  };
};

export const componentTrustedContactsToAppTrustedContactsMapper = (
  appTrustedContact,
  disposition
) => {
  return {
    ...defaultTrustedContactAppState,
    disposition,
    ...appTrustedContact
  };
};

export const componentEmailToAppEmailMapper = (componentEmail, appEmail) => {
  const trimmedEmail = trimString(componentEmail.email);

  if (appEmail.address !== trimmedEmail) {
    const isAdding =
      !appEmail.address && trimmedEmail && !isEmpty(trimmedEmail);

    if (isAdding) {
      return {
        address: trimmedEmail,
        disposition: {
          action: 'add'
        },
        typeId: PRIMARY_CONTACT
      };
    }

    return {
      ...appEmail,
      address: trimmedEmail,
      disposition: {
        action: 'update'
      }
    };
  }
  return appEmail;
};

export const DIVIDEND_TYPE_ID = 10;
export const CAPITAL_GAINS_TYPE_ID = 20;

export const REINVEST_ID = 101;
export const DISTRIBUTE_TO_FUND_ID = 102;
export const DISTRIBUTE_TO_BANK_ID = 103;
export const TO_CHECK_ID = 104;
export const CASH_ID = 105;
export const SWEEP_ID = 106;

export const distributionNameMap = {
  101: 'Reinvest',
  102: 'To Fund',
  103: 'To Bank',
  104: 'By Check',
  105: 'Cash',
  106:'Sweep'
};

export const SHORT_PENDING = 2;
export const LONG_PENDING = 3;

export const isPendingUpdate = (pendingUpdateId) =>
  pendingUpdateId === SHORT_PENDING || pendingUpdateId === LONG_PENDING;

export const documentDeliveryServiceTypesMap = {
  6: 'prospectus',
  8: 'statements',
  64: 'yepaperstatements',
  70: 'confirmations',
  80: 'uacollegesavingsstatements',
  81: 'uacollegesavingsconfirms',
  82: 'trpcollegesavingsstatements',
  83: 'trpcollegesavingsconfirms',
  90: 'marylandcollegesavingsstatements',
  91: 'marylandcollegesavingsconfirms',
  97: 'proxy',
  104: 'uacollegesavingspdd',
  105: 'trpccollegesavingspdd',
  106: 'marylandcollegesavingspdd',
  117: 'taxforms',
  118: 'clientcommunications',
  119: 'trpclientcommunications',
  120: 'uaclientcommunications',
  121: 'marylandclientcommunications',
  122: 'brokeragetaxforms',
  123: 'brokerageprospectuses',
  124: 'brokerageproxy',
  125: 'brokerageclientcommunications',
  127: 'uataxforms',
  128: 'trptaxforms',
  129: 'marylandtaxforms'
};

export const documentDeliveryServiceTypesMapByName = entries(
  documentDeliveryServiceTypesMap
).reduce((acc, [id, name]) => ({ ...acc, [name]: Number(id) }), {});

export const documentDeliveryServiceTypeIds = keys(
  documentDeliveryServiceTypesMap
).map(Number);

export const serviceFeeWaiverTypeIds = [8, 70, 6];

export const PROSPECTUS_SERVICE_TYPE_ID = 6;
export const STATEMENTS_SERVICE_TYPE_ID = 8;
export const CONFIRMATIONS_SERVICE_TYPE_ID = 70;

export const YE_PAPER_STATEMENTS_SERVICE_TYPE_ID = 64;

export const generalGroupIdMap = {
  // The object is to provide a human readable form for JUST the serviceTypeIds
  prospectus: documentDeliveryServiceTypesMapByName.prospectus,
  statements: documentDeliveryServiceTypesMapByName.statements,
  confirmations: documentDeliveryServiceTypesMapByName.confirmations
};

export const documentDeliveryInitialState = (
  isSalesforceRepEnabled = false
) => {
  return {
    generalInvesting: [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.statementsModalCopy(isSalesforceRepEnabled),
        serviceTypeId: 8,
        isEligible: function (views) {
          // MF or Brokerage or Trac - ask about non-century trac
          // owned
          // logged in user is primary party
          return views.isEligibleForRetailStatements;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.confirmationsModalCopy(isSalesforceRepEnabled),
        serviceTypeId: 70,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        }
      },
      {
        title: 'Prospectuses and Shareholder Reports',
        note: 'Note: If Confirmations are delivered by Email, Prospectuses and Shareholder Reports must also be delivered by Email.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.prospectusModalCopy(isSalesforceRepEnabled),
        serviceTypeId: 6,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        },
        hasBrokerageCopy: true,
        showBanner: true
      },
      {
        title: 'Proxy Materials',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.proxyModalCopy(isSalesforceRepEnabled),
        serviceTypeId: 97,
        isEligible: function (views) {
          // MF
          return views.isEligibleForRetailProxy;
        },
        hasBrokerageCopy: true
      }
    ],
    trpCollegeSavings: [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.statementsModalCopy529(isSalesforceRepEnabled),
        serviceTypeId: [82],
        isEligible: function (views) {
          // National 529
          // Owned and Primary || Releated
          // Not UGMA UTMA
          return views.isEligibleForNationalPaperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.confirmationsModalCopy529(isSalesforceRepEnabled),
        serviceTypeId: [83],
        isEligible: function (views) {
          // National 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForNationalPaperless;
        }
      },
      {
        title: 'Plan Disclosure Documents',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.planDisclosureStatementForNatAndAk529s(
          isSalesforceRepEnabled
        ),
        serviceTypeId: [105],
        isEligible: function (views) {
          // National 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForNationalPaperless;
        }
      }
    ],
    mdCollegeSavings: [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.statementsModalCopy529(false),
        serviceTypeId: [90],
        isEligible: function (views) {
          // MD 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForMarylandPaperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.confirmationsModalCopy529(false),
        serviceTypeId: [91],
        isEligible: function (views) {
          // MD 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForMarylandPaperless;
        }
      },
      {
        title: 'Plan Disclosure Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.planDisclosureStatementForMd529,
        serviceTypeId: [106],
        isEligible: function (views) {
          // MD 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForMarylandPaperless;
        }
      }
    ],
    akCollegeSavings: [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.statementsModalCopy529(false),
        serviceTypeId: [80],
        isEligible: function (views) {
          // Alaska 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForAlaskaPaperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.confirmationsModalCopy529(false),
        serviceTypeId: [81],
        isEligible: function (views) {
          // Alaska 529
          // Owned and Primary || Related
          // Not UGMA UTMA
          return views.isEligibleForAlaskaPaperless;
        }
      },
      {
        title: 'Plan Disclosure Documents',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: Content.planDisclosureStatementForNatAndAk529s(false),
        serviceTypeId: [104],
        isEligible: function (views) {
          // Alaska 529
          // Owned and Primary || Releated
          // Not UGMA UTMA
          return views.isEligibleForAlaskaPaperless;
        }
      }
    ],
    activePlus: [
      {
        disableMailSelection: true,
        title: 'All Clients Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        modalContent: Content.accountCommunicationsCopy,
        isEmailDelivery: true,
        isReadOnly: true,
        isEligible: function (views) {
          // hasAPP
          // service feature 88 not present
          return views.hasActivePlus;
        }
      }
    ],
    activeAdvisory: [
      {
        disableMailSelection: true,
        title: 'All Clients Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        modalContent: Content.accountCommunicationsCopy,
        isEmailDelivery: true,
        isReadOnly: true,
        isEligible: function (views) {
          // hasActiveAdvisory
          return views.hasActiveAdvisory;
        }
      }
    ]
  };
};

export const filterBrokerageOptions = ({ serviceTypeId }) => {
  return (
    serviceTypeId === documentDeliveryServiceTypesMapByName.statements ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.confirmations ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.brokerageprospectuses ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.brokerageproxy
  );
};

export const filterMutualFundOptions = ({ serviceTypeId }) => {
  return (
    serviceTypeId === documentDeliveryServiceTypesMapByName.statements ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.confirmations ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.prospectus ||
    serviceTypeId === documentDeliveryServiceTypesMapByName.proxy
  );
};

const broadAssetClassNameMap = {
  10: 'Domestic Stock Funds',
  20: 'International Stock Funds',
  30: 'Money Market Funds',
  40: 'Tax-Free Bond Funds',
  50: 'Taxable Bond Funds',
  60: 'Variable Annuity',
  70: 'My T. Rowe Price Funds'
};

export const getBroadAssetClassName = (broadAssetClassId) =>
  broadAssetClassNameMap[broadAssetClassId];
export const getBroadAssetClassId = (broadAssetClassName) => {
  return Number(
    keys(broadAssetClassNameMap).find(
      (key) => broadAssetClassNameMap[key] === broadAssetClassName
    )
  );
};

export const TAX_FORM_TYPE_ID = 9;
export const INVESTOR_EMAIL_TYPE_ID = 4221;
export const TRP_REPORT_EMAIL_TYPE_ID = 305;

export const EDUCATION_INSIGHTS_CATEGORY_ID = 127;
export const PRODUCTS_SERVICES_CATEGORY_ID = 128;
export const RESEARCH_SURVEYS_CATEGORY_ID = 126;
export const OPT_OUT_CATEGORY_ID = 125;

export const optOutCategories = [
  {
    key: 'educationInsights',
    categoryId: EDUCATION_INSIGHTS_CATEGORY_ID,
    categoryName: 'Education & Insights'
  },
  {
    key: 'productsServices',
    categoryId: PRODUCTS_SERVICES_CATEGORY_ID,
    categoryName: 'Products & Services'
  },
  {
    key: 'researchSurveys',
    categoryId: RESEARCH_SURVEYS_CATEGORY_ID,
    categoryName: 'Research & Surveys'
  },
  {
    key: 'optOutAll',
    categoryId: OPT_OUT_CATEGORY_ID,
    categoryName: 'All Retail Commercial Emails'
  }
];

export const NO_SUBSCRIPTION_TYPE_ID = -1;
export const MY_FUNDS_SUBSCRIPTION_TYPE_ID = 476;
export const ALL_FUNDS_SUBSCRIPTION_TYPE_ID = 477;
export const SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID = 480;

export const DAILY_PRICES_TYPE_ID = 300;
export const MONTHLY_FUND_TYPE_ID = 301;

export const MARKET_WRAP_UP_IDS = [302, 303, 304, 306, 307, 308];

export const TRP_INVESTOR_DIGEST = 309;

export const CONTACT_INFO_EVENT_TYPES = {
  ADD_PHONE: 'ADD_PHONE_NUMBER',
  DELETE_PHONE: 'DELETE_PHONE_NUMBER',
  UPDATE_PHONE: 'UPDATE_PHONE_NUMBER',
  UPDATE_POSTAL_ADDRESS: 'UPDATE_POSTAL_ADDRESS'
};

export const mailingTypes = {
  residential: 'residential',
  mailing: 'mailing'
};

export const isResidentialAddressType = (hasResidential, type) =>
  !hasResidential || type === mailingTypes.residential;

export const buildContactInfoBusinessEvent = (
  view,
  email,
  bpid,
  uberId,
  eventType
) => {
  const { hasMaryland529Only, hasAlaska529Only, hasNational529Only } = view;

  const clientSegment = hasMaryland529Only
    ? 'MD529'
    : hasAlaska529Only
    ? 'UA529'
    : hasNational529Only
    ? 'TRP529'
    : 'RETAIL';

  return {
    eventType: eventType,
    emailAddress: email,
    brandingProfile: clientSegment,
    entityKey: {
      businessPartyId: bpid,
      uberId: uberId
    }
  };
};

export const applyAccountNumbersToContacts = (updatedAllTrustedContacts, accountsById) => {
  return updatedAllTrustedContacts.map(contact => {
    const account = accountsById[contact.accountId]
    if (account) {
      const {accountNumber} = account;
      return {
        ...contact,
        accountNumber
      };
    }
  })
}

export const getAccountFundAlertsSelectedType = (
  key,
  value,
  selection,
  hasTracOnly
) => {
  if (
    (toLower(key).indexOf('performance') !== -1 ||
      toLower(key).indexOf('price') !== -1) &&
    typeof value === 'number'
  ) {
    return value;
  }

  if (selection && selection.typeId && selection.typeId > 0) {
    return selection.typeId;
  }

  if (hasTracOnly) {
    return ALL_FUNDS_SUBSCRIPTION_TYPE_ID;
  }

  return MY_FUNDS_SUBSCRIPTION_TYPE_ID;
};

export const isNewPhoneByType = (phoneNumbersOnFile, phoneType) => {
  return !phoneNumbersOnFile.some((phoneObj) => phoneObj.typeId === phoneType);
};

export const getComplianceSubscriptions = (paperlessDelivery, loadedPaperlessDelivery,paperlessPreferencesSettings) => {
  const preferences = flatten(
    Object.entries(paperlessPreferencesSettings).map(
      ([section, deliveryPreferences]) =>
        deliveryPreferences.map((pref) => {
          return { ...pref, section };
        })
    )
  );

  const complianceSubscriptions = preferences
    .filter((preference) =>
      removeUnchangedSelections(
        preference,
        loadedPaperlessDelivery,
        {...paperlessDelivery}
      )
    )
    .map(({ serviceTypeId }) => {
      return {
        subscriptionId: Number(serviceTypeId),
        subscribed: Boolean(paperlessDelivery[serviceTypeId])
      };
    });

  return uniqBy(complianceSubscriptions, ({subscriptionId})=> subscriptionId)
}

export const  BrokerageNote = `This option is only available to clients who have selected to receive statements via email.`;

export const  mutualFundsNote = `This option is only available to clients who have selected to receive statements via email and will also apply to Brokerage Account statements since they are combined.
Selecting this option does not forfeit the Account Fee Waiver.`;